import { services } from '@lifetools/shared-services';
import { resolveServerTimestamps } from '@lifetools/shared-utils-time';
import { callTriggers } from '../triggers/callTriggers';
import { IDocumentData } from './types/IDocumentData';
import { Id } from './types/Id';
import { toDatabaseJson } from './utils/toDatabaseJson';
import { getDocument } from './getDocument';

/**
 * A single document update within a batch: the `collection` and `id` identifying the document,
 * and the `values` to update (any `undefined` values will be deleted).
 */
export interface IDocumentUpdate {
  collection: string;
  id: Id;
  values: IDocumentData;
}

/**
 * Updates multiple documents in a single atomic batch commit.
 *
 * Unlike issuing sequential {@link updateDocument} calls, either every update commits or none
 * does, and observers receive a single consistent snapshot per document rather than a series of
 * intermediate states. Use this when several documents must change together — e.g. a schedule
 * view and the intent starts it was derived from.
 *
 * Each update runs the same `before` triggers as {@link updateDocument}. All documents must
 * already exist; there is no `forceCreate` equivalent.
 *
 * @param updates - The document updates to apply atomically
 *
 * @returns the values sent for each update (in the same order), including any changes made by
 *          triggers
 */
export async function updateDocuments(updates: IDocumentUpdate[]): Promise<IDocumentData[]> {
  const batch = services.database.batch();

  const prepared = await Promise.all(updates.map(async ({ collection, id, values }) => {
    const docRef = services.database.collection(collection).doc(id);
    const before = await getDocument(collection, id, { noSubscription: true });

    if (!before) {
      throw new Error(`Cannot batch-update '${collection}/${id}': the document does not exist`);
    }

    const toSave = await callTriggers('before', 'update', collection, id, values, before);

    if (!toSave) {
      throw new Error(
        `The 'before' triggers for 'update' on '${collection}/${id}' returned no values`
      );
    }

    return { docRef, toSave };
  }));

  for (const { docRef, toSave } of prepared) {
    batch.update(docRef, toDatabaseJson(toSave));
  }

  await batch.commit();

  return prepared.map(({ toSave }) => resolveServerTimestamps(toSave));
}
